import { useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import {
  User,
  CreateUserData,
  UpdateUserData,
  UserRole,
  ROLE_LABELS,
  ROLE_LEVELS,
  roleLabel,
} from "@/services/user-service";
import { Company, fetchCompanies } from "@/services/company-service";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { onlyNumbers } from "@/utils/formatters";

interface CreateEditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: User | null;
  onSave: (
    data: CreateUserData | UpdateUserData,
    userId?: string
  ) => Promise<void>;
}

/**
 * Regra N+1: um usuário de nível N só cria usuários de nível N+1.
 * Retorna a lista de roles que o actor pode criar.
 */
function rolesCreatableByLevel(actorLevel: number | undefined): UserRole[] {
  if (actorLevel === undefined || actorLevel === null) return [];
  const targetLevel = actorLevel + 1;
  return (Object.entries(ROLE_LEVELS) as Array<[UserRole, number]>)
    .filter(([, level]) => level === targetLevel)
    .map(([role]) => role);
}

export function CreateEditUserDialog({
  open,
  onOpenChange,
  user,
  onSave,
}: CreateEditUserDialogProps) {
  const { user: loggedUser } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [role, setRole] = useState<UserRole | "">("");
  const [companyId, setCompanyId] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!user;
  const isSuperAdmin = loggedUser?.role === "super_admin";

  // Roles que o logado pode criar (N+1). Vazio = ele não cria ninguém.
  const creatableRoles = useMemo(
    () => rolesCreatableByLevel(loggedUser?.level),
    [loggedUser?.level]
  );

  // Super_admin escolhe a empresa (não tem company_id próprio); demais níveis
  // criam usuários na própria empresa.
  const showCompanySelect = isSuperAdmin;

  useEffect(() => {
    if (!open || !showCompanySelect) return;
    fetchCompanies(false, false, undefined, 1, 100)
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, [open, showCompanySelect]);

  // Reset / pré-preenchimento ao abrir
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setCellPhone(user.cellphone || "");
      setRole((user.role as UserRole) || "");
      setCompanyId(user.company_id ?? "");
    } else {
      setName("");
      setEmail("");
      setCellPhone("");

      // Se há exatamente uma role criável, fixa; caso contrário, deixa em branco pro select
      if (creatableRoles.length === 1) {
        setRole(creatableRoles[0]);
      } else {
        setRole("");
      }

      // company_id default: dos criadores não-super, usa a própria; super escolhe depois
      if (!isSuperAdmin) {
        setCompanyId(loggedUser?.company_id ?? "");
      } else {
        setCompanyId("");
      }
    }
    setErrors({});
  }, [user, open, loggedUser, isSuperAdmin, creatableRoles]);

  const handleCellPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 11) val = val.slice(0, 11);
    val = val.replace(/^(\d{2})(\d)/g, "($1) $2");
    val = val.replace(/(\d{5})(\d)/, "$1-$2");
    setCellPhone(val);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Nome é obrigatório";
    if (!email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i.test(
        email
      )
    ) {
      newErrors.email = "Email inválido";
    }
    const cleanCellPhone = onlyNumbers(cellPhone);
    if (cleanCellPhone.length !== 11) {
      newErrors.cellPhone = "Telefone deve ter 11 dígitos";
    }
    if (!isEdit) {
      if (!role) {
        newErrors.role = "Selecione a função";
      }
      // Exige empresa se o role novo requer vínculo (todos exceto super_admin)
      if (role && role !== "super_admin" && !companyId) {
        newErrors.companyId = "Selecione a empresa";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      if (isEdit && user) {
        const updatePayload: UpdateUserData = {
          name,
          email,
          cellphone: onlyNumbers(cellPhone),
        };
        if (isSuperAdmin && companyId) {
          updatePayload.company_id = companyId;
        }
        await onSave(updatePayload, user.id);
      } else {
        const createPayload: CreateUserData = {
          name,
          email,
          cellphone: onlyNumbers(cellPhone),
          role: role as UserRole,
          company_id: companyId || null,
        };
        await onSave(createPayload);
      }
      onOpenChange(false);
    } catch {
      // erro tratado pelo pai
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[780px]">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">
            {isEdit ? "Editar Usuário" : "Novo Usuário"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="filter-label" htmlFor="name">
              Nome *
            </Label>
            <Input
              className="filter-input"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome completo"
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-red-600 text-xs">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="email">
                E-mail *
              </Label>
              <Input
                className="filter-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-red-600 text-xs">{errors.email}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cellPhone">
                Telefone *
              </Label>
              <InputMask
                mask="(99) 99999-9999"
                value={cellPhone}
                onChange={handleCellPhoneChange}
                maskChar={null}
              >
                {(inputProps: any) => (
                  <Input
                    className="filter-input"
                    {...inputProps}
                    id="cellPhone"
                    placeholder="(00) 00000-0000"
                    aria-invalid={!!errors.cellPhone}
                  />
                )}
              </InputMask>
              {errors.cellPhone && (
                <p className="text-red-600 text-xs">{errors.cellPhone}</p>
              )}
            </div>
          </div>

          {/* Role e empresa */}
          <div className="grid grid-cols-2 gap-3">
            {/* Role na criação: respeita regra N+1. Editar não altera role. */}
            {!isEdit && (
              <div className="space-y-1">
                <Label className="filter-label" htmlFor="role">
                  Função *
                </Label>
                {creatableRoles.length === 1 ? (
                  <div className="filter-input px-3 py-2 bg-muted rounded-md text-sm">
                    {roleLabel(creatableRoles[0])}
                  </div>
                ) : creatableRoles.length > 1 ? (
                  <Select
                    value={role}
                    onValueChange={(v) => setRole(v as UserRole)}
                  >
                    <SelectTrigger id="role" className="filter-input">
                      <SelectValue placeholder="Selecione a função" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50">
                      {creatableRoles.map((r) => (
                        <SelectItem key={r} value={r}>
                          {roleLabel(r)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="filter-input px-3 py-2 bg-muted rounded-md text-sm text-muted-foreground">
                    Seu perfil não pode criar novos usuários.
                  </div>
                )}
                {errors.role && (
                  <p className="text-red-600 text-xs">{errors.role}</p>
                )}
              </div>
            )}

            {/* Empresa: só super_admin escolhe. Demais níveis usam a própria. */}
            {showCompanySelect && (
              <div className="space-y-1">
                <Label className="filter-label" htmlFor="companyId">
                  Empresa *
                </Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger id="companyId" className="filter-input">
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover z-50">
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.companyId && (
                  <p className="text-red-600 text-xs">{errors.companyId}</p>
                )}
              </div>
            )}
          </div>

          {isEdit && user?.role && (
            <p className="text-xs text-muted-foreground">
              Função atual: <span className="font-medium">{roleLabel(user.role)}</span>{" "}
              (não editável)
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            className="btn-header"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            className="btn-header"
            onClick={handleSave}
            disabled={!isEdit && creatableRoles.length === 0}
          >
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
